import React, { useState, useEffect } from 'react';
import VehicleInfo from '../vehicle-info/vehicle-info';
import VehicleInfoSkeleton from '../vehicle-info-skeleton/vehicle-info-skeleton';
import { VehicleDetail } from '../../../../../models/profile-with-vehicles';
import ProfileService from '../../../../../services/profile-service/profile-service';
import './vehicle-list.scss';
import { FMButton, FMModal } from '@own/fds-react';
import { useOSBContentFragment } from '../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import ErrorDisplay from '../../common/error-display/error-display';
import VehicleSearchForm from '../vehicle-search-form/vehicle-search-form';
import FordAccountVehicle from '../ford-account-vehicle/ford-account-vehicle';

interface VehicleListProps {
    profileService?: { request: () => Promise<{ vehicles: VehicleDetail[] }> };
}

const VehicleList: React.FC<VehicleListProps> = ({ profileService }) => {
    const [vehicles, setVehicles] = useState<VehicleDetail[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [vehiclesFetchDone, setVehiclesFetchDone] = useState(false);
    const [vehiclesSuccess, setVehiclesSuccess] = useState(false);
    const vehicleStepContent =
        useOSBContentFragment('common/osb-v3', 'vehicle-step') || {};
    const osbContentData =
        useOSBContentFragment('common/osb-v3', 'vehicle-step-extension') || {};

    const isAemReady = React.useMemo(
        () => !!osbContentData && Object.keys(osbContentData).length > 0,
        [osbContentData]
    );

    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [showVehicleInfo, setShowVehicleInfo] = useState(false);
    const [vehicleInfo, setVehicleInfo] = useState<any>(null);
    const [vehicleImageUrl, setVehicleImageUrl] = useState<string | undefined>(
        undefined
    );

    useEffect(() => {
        const fetchVehicles = async () => {
            const svc = profileService || new ProfileService();
            try {
                const { vehicles } = await svc.request();
                setVehicles(vehicles);
                setVehiclesSuccess(true);
                setVehiclesFetchDone(true);
            } catch (e) {
                setError('Failed to fetch vehicles from profile');
                setVehiclesSuccess(false);
                setVehiclesFetchDone(true);
            }
        };
        fetchVehicles();
    }, [profileService]);

    useEffect(() => {
        if (!isAemReady) return; // need AEM first
        if (vehiclesFetchDone && loading) {
            setLoading(false);
        }
    }, [isAemReady, vehiclesFetchDone, loading]);

    const togglePopup = () => {
        console.log('togglePopup called');
        setIsPopupVisible(prev => !prev);
    };

    const handleVehicleInfoSuccess = (info: any, imageUrl: string) => {
        setVehicleInfo(info);
        setVehicleImageUrl(imageUrl);
        setShowVehicleInfo(true);
    };
    const Popup = () => (
        <FMModal
            data-testid="modal"
            name={'vehicle-search-popup'}
            isVisible={isPopupVisible}
            onClose={togglePopup}
        >
            <VehicleSearchForm
                onContinue={togglePopup}
                onVehicleInfoSuccess={handleVehicleInfoSuccess}
            />
        </FMModal>
    );

    const renderContent = () => {
        if (loading) {
            return (
                <div className="osb-v3-vehicle-info-skeleton-wrapper" data-testid="vehicle-info-skeleton">
                    <VehicleInfoSkeleton />
                    <VehicleInfoSkeleton />
                </div>
            );
        }

        // At this point AEM is ready and vehicles fetch finished
        if (!vehiclesSuccess && error) {
            return (
                <div className="osb-v3-vehicle-info-error-display">
                    <ErrorDisplay
                        title={`${osbContentData?.errorMessageTitle}`}
                        errorMessage={error}
                        suggestion={`${osbContentData?.errorMessageSuggestion}`}
                    />
                </div>
            );
        }

        return (
            <div className="osb-v3-vehicle-info-scroll-container">
                {vehicles.map(vehicle => (
                    <VehicleInfo key={vehicle.vin} vehicleDetail={vehicle} />
                ))}
            </div>
        );
    };

    return (
        <>
            <div className="osb-v3-vehicle-info-heading">
                {isAemReady ? osbContentData?.vehicleInformationLabel : ''}
            </div>
            {renderContent()}
            <div className="osb-v3-add-vehicle-row">
                <span className="vehicle-label">
                    {isAemReady ? osbContentData?.vehicleNotListedLabel : ''}
                </span>
                <FMButton
                    data-testid="closeButton"
                    className="add-vehicle-button"
                    theme="light"
                    type="primary"
                    label={isAemReady ? osbContentData?.addAVehicleButtonLabel : ''}
                    onClick={togglePopup}
                    disabled={loading || !isAemReady}
                />
            </div>
            {isPopupVisible && <Popup />}
            {/* Modal for displaying FordAccountVehicle */}
            <FMModal
                data-testid="vehicle-search-popup"
                title={String(vehicleStepContent?.addAVehicleTitleLabel)}
                name="osb-v3-vehicle-detail-info"
                isVisible={showVehicleInfo}
                onClose={() => setShowVehicleInfo(false)}
            >
                {vehicleInfo && (
                    <FordAccountVehicle
                        vehicleInfo={vehicleInfo}
                        vehicleImageUrl={vehicleImageUrl || ''}
                    />
                )}
            </FMModal>
        </>
    );
};

export default VehicleList;
